/**
 * Putting an organisation's branding on a page.
 *
 * ── Why this is a shared file and not three implementations ─────────────────
 * Every product site does the same four things — set the accent's custom
 * properties on `documentElement`, put the organisation's name in the tab, hand
 * the chrome its word and its mark, and remember all of it so the next load
 * does not flash Mauvely before `/me` answers. Three copies of that would
 * drift, and a brand applied differently on Cloud and on Review is a product
 * that looks like two products.
 *
 * Owned here and copied verbatim into website-enterprise, website-cloud, scope
 * and review, like `branding.ts` beside it — propagate with
 * `node scripts/sync-shared.mjs` from the workspace repo, and
 * `shared/copies.test.ts` in each of the five pins it. Browser-only: it touches
 * `document` and `localStorage` and is imported by a site's state provider, not
 * by a Worker.
 *
 * ── Why the word and the mark are a store, and not props ────────────────────
 * The accent reaches the page as CSS custom properties on the root element —
 * a global, read by every rule that references them, threaded through nothing.
 * The word and the mark want exactly the same reach: Review draws its lockup
 * in ten places, Scope in six. Passing `branding` down to each would put a
 * prop on ten components to change one word, and the first call site anybody
 * forgot would render Mauvely inside a customer's product.
 *
 * So they are a module-level value with a subscription, read through
 * `useSyncExternalStore`. Same global as the accent, one layer up.
 *
 * ── The cache is what stops the flash ───────────────────────────────────────
 * A branded site loads Mauvely's stylesheet, then asks who the viewer is, then
 * repaints. Without a cache that is a violet flash — and now a wrong *word* —
 * on every navigation for a customer who has paid for neither. The cached
 * value is a presentation preference and nothing else, the same class of thing
 * as the theme key already stored beside it, and it is re-derived from the
 * server on every load, so a stale one survives exactly one paint.
 */

import { accentVariables, productName, type Branding } from './branding';

const KEY = 'mauvely-branding';

/** The product this bundle is. Set once at startup by the site's entry point;
 *  it is what lets `applyBranding` keep the tab title in step without every
 *  caller repeating the app record. */
let product: { name: string; short: string } | null = null;

/** Name this product. Call once, at module scope, before `applyCachedBranding`. */
export function setProduct(app: { name: string; short: string }): void {
  product = app;
}

// ── the store ───────────────────────────────────────────────────────────────

let current: Branding | null = null;
const listeners = new Set<() => void>();

/** The branding in force, or null for Mauvely. */
export function getBranding(): Branding | null {
  return current;
}

/** Subscribe to changes. Pairs with `getBranding` under `useSyncExternalStore`. */
export function subscribeBranding(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Whether two brandings say the same thing.
 *
 * `applyBranding` is called on **every** `/me`, and each one parses fresh JSON
 * — so the object identity always differs even when nothing has. Without this
 * comparison `useSyncExternalStore` would see a new snapshot on every poll and
 * re-render the whole chrome for no reason, which is the documented way to turn
 * that hook into an infinite loop.
 */
function same(a: Branding | null, b: Branding | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.name === b.name &&
    a.word === b.word &&
    a.accent === b.accent &&
    a.logoUrl === b.logoUrl
  );
}

function publish(branding: Branding | null): void {
  if (same(current, branding)) return;
  current = branding;
  for (const fn of listeners) fn();
}

// ── the accent ──────────────────────────────────────────────────────────────

/** Every property `accentVariables` can set, so removal is exact rather than
 *  a guess at what was applied. */
function clear(root: HTMLElement): void {
  // Derived from a known-valid colour: the *names* are what matter and they do
  // not depend on the value.
  for (const name of Object.keys(accentVariables('#796cbf'))) {
    root.style.removeProperty(name);
  }
}

/**
 * Apply an accent, or remove one.
 *
 * Safe to call with the same value repeatedly — it sets properties rather than
 * appending a stylesheet, so there is nothing to accumulate.
 */
export function applyAccent(accent: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (!accent) {
    clear(root);
    return;
  }

  for (const [name, value] of Object.entries(accentVariables(accent))) {
    root.style.setProperty(name, value);
  }
}

// ── the title ───────────────────────────────────────────────────────────────

/**
 * The organisation's name for this product, in the tab.
 *
 * Each site ships a static `<title>` in its `index.html` — "Mauvely Cloud" and
 * the rest — which stays as the answer before anybody has signed in. This
 * replaces it once there is a member to ask about, and puts it back when there
 * is not.
 *
 * A no-op until `setProduct` has been called: a site that has not named itself
 * has nothing to brand, and guessing from the existing title would turn
 * "Mauvely Cloud" into a customer's name for a page that is still Mauvely's.
 */
export function applyTitle(branding: Branding | null | undefined): void {
  if (typeof document === 'undefined' || !product) return;
  document.title = productName(branding ?? null, product);
}

// ── the whole thing ─────────────────────────────────────────────────────────

/**
 * What the last load saw, applied before the network answers.
 *
 * Call at module scope in a site's entry point, after `setProduct`. Returns
 * what it applied, or null.
 */
export function applyCachedBranding(): Branding | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as Branding;
    applyAccent(cached.accent);
    applyTitle(cached);
    publish(cached);
    return cached;
  } catch {
    // A browser with storage disabled, or a value from an older shape, gets one
    // Mauvely paint. Not worth failing a page load over.
    return null;
  }
}

/**
 * Apply what the server said, and remember it for the next load.
 *
 * `null` branding clears everything, which is what a member removed from an
 * organisation — or one whose administrator turned branding off — has to see on
 * their very next navigation rather than eventually.
 */
export function applyBranding(branding: Branding | null | undefined): void {
  const next = branding ?? null;
  applyAccent(next?.accent ?? null);
  applyTitle(next);
  publish(next);
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // See above.
  }
}
